/**
 * Activity search request builders (TRAV23 activities-front merge).
 *
 * The activities front defined these as `getSearchBody`, `getFilters`,
 * `getRequestPayload`, `getOccupancies`, `getFilterPayload` and `getPagePayload` in
 * `@share/utils/search`. All six of those names already exist here for hotels with
 * different signatures, so the activity versions live in their own module.
 */

import get from 'lodash/get';

import {
  IActivityFilterPayloadRequest,
  IActivityOccupanciesRequest,
  IActivityPagePayloadRequest,
  IActivitySearchFiltersRequest,
  IActivitySearchPayload,
  IActivitySearchRequest,
} from '@common-types';
import { ILocation, IRoom, LocationsTypes } from '@share/common-types';
import { DEFAULT_PAGE_SIZE } from '@share/constants';
import type { IActivityFiltersState, IActivityState } from '@share/store/slices';

const ALL_RATINGS = [1, 2, 3, 4, 5];

/**
 * Mirrors `DEFAULT_ADULT_AGE` in the rooms slice. Restated rather than imported:
 * `isolatedModules` keeps a value import as a runtime edge, and this module is
 * reached from the store, so importing the barrel back would close a cycle.
 */
const DEFAULT_ADULT_AGE = 18;

export const getActivityFilters = (
  filtersStore: IActivityFiltersState,
): IActivitySearchFiltersRequest => {
  const {
    budget,
    selectedAutocompleteItem,
    accommodationType,
    boardNames,
    refundable,
    amenities,
    distance,
    ratings,
    neighbourhoods,
    flexibleDates,
    accessibility,
    popularLocations,
    hideRatings,
    timeOfDays,
    specials,
    guideLanguages,
    durations,
    activityCategory,
  } = filtersStore;

  // `hideRatings` can be a boolean (hide the control) or a list of ratings the account
  // suppresses. In the latter case an empty selection means "everything not hidden",
  // which has to be sent explicitly - the API treats an empty list as no filter.
  const isHideRatingsNumbers =
    hideRatings && Array.isArray(hideRatings) && hideRatings.every((item) => typeof item === 'number');
  const ratingsFiltered =
    ratings?.length || !isHideRatingsNumbers
      ? ratings
      : ALL_RATINGS.filter(
          (item: number) => !hideRatings || !Array.isArray(hideRatings) || !hideRatings.includes(item),
        );

  const filters = {
    budgetRanges: budget.map((range: string) => {
      const [from, to]: string[] = range.split('-');

      return { from: +from || null, to: +to || null };
    }),
    name: selectedAutocompleteItem ? selectedAutocompleteItem.name : null,
    code: selectedAutocompleteItem ? selectedAutocompleteItem.activityCode : null,
    accommodationType,
    boardNames,
    refundable,
    amenities,
    ratings: ratingsFiltered,
    distanceInMiles: distance,
    neighbourhoods,
    accessibilities: accessibility,
    popularLocations,
    // Omitted rather than sent empty: the API widens the search on an empty facet list.
    timeOfDays: timeOfDays?.length ? timeOfDays : undefined,
    specials: specials?.length ? specials : undefined,
    guideLanguages: guideLanguages?.length ? guideLanguages : undefined,
    durations: durations?.length ? durations : undefined,
    categories: activityCategory?.trim() ? [activityCategory.trim()] : undefined,
  } as IActivitySearchFiltersRequest;

  const flexibleDate = get(flexibleDates, '[0]');
  filters.exactSearch = !flexibleDate || flexibleDate === 'true';

  return filters;
};

/**
 * Activities price per adult by age, so each adult is sent individually rather than as
 * a count.
 *
 * A room can still reach here without ages - `getNormalizedRooms` rebuilds rooms from
 * the `rooms` URL parameter, which only carries counts - so the count is the fallback.
 * Sending no adults makes the API return an empty result set rather than an error.
 */
export const getActivityOccupancies = (rooms: IRoom[]): IActivityOccupanciesRequest => {
  const adults: number[] = [];
  const kidAges: number[] = [];

  rooms.forEach((room) => {
    const ages = (room.adultAges ?? []).filter((age): age is number => typeof age === 'number');

    adults.push(
      ...(ages.length ? ages : Array(room.adultsCount ?? 0).fill(DEFAULT_ADULT_AGE)),
    );

    room.kids?.forEach((kid) => {
      if (typeof kid.age === 'number') {
        kidAges.push(kid.age);
      }
    });
  });

  return {
    adults,
    KidAges: kidAges,
    Total: adults.length + kidAges.length,
  };
};

export const getActivityFilterPayload = (location: ILocation): IActivityFilterPayloadRequest => {
  if (location.type === LocationsTypes.Activity) {
    return {
      Type: 'activityCode',
      Value: location.code?.toString() ?? '',
    };
  }

  return {
    Type: 'destination',
    Value: location.code?.toString() ?? '',
  };
};

export const getActivityPagePayload = (
  pageNumber: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
): IActivityPagePayloadRequest => {
  return {
    number: pageNumber,
    size: pageSize,
  };
};

export const getActivityRequestPayload = (
  residency: string,
  currency: string,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  rooms: IRoom[],
  location: ILocation,
): IActivitySearchRequest => {
  return {
    Residency: residency,
    Currency: currency,
    From: startDate,
    // Activity search became a date range upstream; a single-day pick sends the same
    // day on both ends.
    To: endDate ?? startDate,
    Occupancies: getActivityOccupancies(rooms),
    cheapest: true,
    Language: 'en',
    Filter: getActivityFilterPayload(location),
  };
};

export const getActivitySearchBody = (
  location: ILocation,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  rooms: IRoom[],
  activityStore: IActivityState,
  filtersStore: IActivityFiltersState,
  isLoadMore: boolean,
  isReuseSessionKey: boolean,
  currency: string,
  lifeStyle: string | null,
  residency: string,
): IActivitySearchPayload => {
  const { sortBy } = filtersStore;

  return {
    Request: getActivityRequestPayload(
      residency ?? 'US',
      currency ?? 'USD',
      startDate,
      endDate,
      rooms,
      location,
    ),
    filter: getActivityFilters(filtersStore),
    page: getActivityPagePayload(activityStore.pageNumber),
    sortBy,
    lifeStyle,
    // Paging and filter changes must stay on the supplier's existing session; a fresh
    // search deliberately omits the key so the backend opens a new one.
    ...(activityStore.sessionKey && (isLoadMore || isReuseSessionKey)
      ? { sessionKey: { ...activityStore.sessionKey } }
      : {}),
  };
};
